import { Col, Form, Input, Row, Select, type FormInstance } from 'antd';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { placeholderField, requiredField } from '@client/utils/form.util';
import { flexLayout } from '@client/utils/layout.util';
import {
  CFeatureSlugPattern,
  EFeatureType,
  slugifyFeatureTitle,
} from '@common/types';

const { TextArea } = Input;

export interface IFeatureInfoProps {
  disabled?: boolean;
  featureTypes?: (keyof typeof EFeatureType)[];
  formRef?: FormInstance;
  isEdit?: boolean;
  slugLocked?: boolean;
}

export const FeatureInfo: React.FC<IFeatureInfoProps> = (props) => {
  const { disabled, featureTypes = [], formRef, isEdit, slugLocked } = props;
  const { t } = useTranslation();

  const slugTouchedRef = useRef(false);
  const title = Form.useWatch('title', formRef);

  useEffect(() => {
    if (!formRef) return;
    if (isEdit) return;
    if (slugTouchedRef.current) return;
    formRef.setFieldsValue({ slug: slugifyFeatureTitle(title ?? '') });
  }, [title, formRef, isEdit]);

  return (
    <>
      <Row gutter={[48, 0]}>
        <Col {...flexLayout.halfColumn}>
          <Form.Item
            label={t('form.typeOf', { entity: t('feature') })}
            name={'type'}
            rules={[requiredField(t('form.typeOf', { entity: t('feature') }))]}
          >
            <Select
              style={{ width: 200 }}
              placeholder={placeholderField(
                t('form.typeOf', { entity: t('feature') }),
              )}
              disabled={disabled}
              options={featureTypes?.sort()?.map((type, idx) => ({
                key: idx,
                label: EFeatureType[type],
                value: type,
              }))}
            />
          </Form.Item>
        </Col>
        <Col {...flexLayout.halfColumn}>
          <Form.Item
            label={t('form.title')}
            name={'title'}
            rules={[requiredField(t('form.title'))]}
          >
            <Input
              type={'text'}
              placeholder={placeholderField(t('form.title'))}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col {...flexLayout.fullColumn}>
          <Form.Item
            label={t('feature.form.slug')}
            name={'slug'}
            tooltip={t('feature.form.slug.helper')}
            rules={[
              requiredField(t('feature.form.slug')),
              {
                pattern: CFeatureSlugPattern,
                message: t('feature.form.slug.invalid'),
              },
            ]}
          >
            <Input
              type={'text'}
              placeholder={placeholderField(t('feature.form.slug'))}
              disabled={disabled || slugLocked}
              onChange={() => {
                slugTouchedRef.current = true;
              }}
            />
          </Form.Item>
        </Col>
        <Col {...flexLayout.fullColumn}>
          <Form.Item label={t('form.description')} name={'description'}>
            <TextArea
              showCount
              maxLength={300}
              rows={4}
              disabled={disabled}
              style={{ resize: 'none' }}
              placeholder={placeholderField(t('form.description'))}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
